/**
 * Отрывок «как ответили»: реплика клиента с возражением и следующие за ней
 * реплики менеджера (#846, экран «Возражения», лучшие отработки).
 *
 * У возражения в анализе нет номера реплики — только цитата, поэтому реплика
 * находится по тексту. Роли в сегментах почти не проставлены (замер 22.09:
 * 640 из 14 749, #987), зато есть метка говорящего ASR. Сегмент с цитатой
 * клиента и называет метку клиента: реплики с ДРУГОЙ меткой после него — это
 * менеджер. Роль сегмента, когда она есть, важнее метки.
 *
 * Сегменты в том виде, в каком их отдаёт `GET /records/:id/transcript`: права
 * на запись проверяет этот маршрут, а не функция.
 */

export interface ExcerptSegment {
  speaker?: string;
  role?: string;
  text?: string;
}

export type ExcerptSpeaker = 'client' | 'agent' | 'unknown';

export interface ExcerptTurn {
  speaker: ExcerptSpeaker;
  text: string;
}

/** Реплик менеджера после цитаты — решение владельца 27.09: «2–3». */
export const EXCERPT_AGENT_TURNS = 3;
/** Всего реплик после цитаты: реплики клиента между ответами тоже идут в отрывок. */
const EXCERPT_MAX_FOLLOWING = 6;
/** Цитата длиннее одного сегмента: ASR режет фразу клиента на соседние куски. */
const MAX_QUOTE_SEGMENTS = 3;
/** Доля слов цитаты, найденных в реплике, при которой пересказ ещё считается ею. */
const MIN_WORD_OVERLAP = 0.7;

function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
}

function roleOf(segment: ExcerptSegment): ExcerptSpeaker {
  if (segment.role === 'agent') return 'agent';
  if (segment.role === 'client' || segment.role === 'customer') return 'client';
  return 'unknown';
}

export interface QuoteMatch {
  /** Первый и последний сегмент цитаты. */
  from: number;
  to: number;
}

/**
 * Где сказана цитата: сначала дословно (в одном сегменте или в нескольких
 * подряд одного говорящего), иначе — пересказ: сегмент, где нашлось не меньше
 * `MIN_WORD_OVERLAP` её слов, а из таких — самый плотный (доля слов цитаты,
 * умноженная на долю сегмента, которую они занимают). Без плотности длинный
 * монолог менеджера, в котором рассыпаны те же частые слова, перебивал бы
 * короткую реплику клиента. Сегмент с ролью менеджера цитатой клиента быть не
 * может; роль вызывающий передаёт уже разрешённой (сегмент или карта ролей
 * анализа).
 *
 * Общая для отрывка и для подсветки цитаты в стенограмме записи: одна цитата
 * должна указывать на одну и ту же реплику в обоих местах.
 */
export function locateQuote(
  segments: readonly ExcerptSegment[],
  quote: string
): QuoteMatch | null {
  const target = words(quote);
  if (target.length === 0) return null;
  const phrase = target.join(' ');
  const texts = segments.map((s) => words(s.text ?? ''));

  for (let from = 0; from < segments.length; from++) {
    if (roleOf(segments[from]!) === 'agent') continue;
    let joined: string[] = [];
    for (let to = from; to < segments.length && to < from + MAX_QUOTE_SEGMENTS; to++) {
      if (to > from && segments[to]!.speaker !== segments[from]!.speaker) break;
      // One label (or none) with per-segment roles: an agent turn is never part
      // of the customer's quote, whatever label it shares.
      if (to > from && roleOf(segments[to]!) === 'agent') break;
      joined = joined.concat(texts[to]!);
      if (` ${joined.join(' ')} `.includes(` ${phrase} `)) return { from, to };
    }
  }

  let bestIndex = -1;
  let bestScore = 0;
  for (let index = 0; index < segments.length; index++) {
    if (roleOf(segments[index]!) === 'agent') continue;
    const segmentWords = texts[index]!;
    if (segmentWords.length === 0) continue;
    const present = new Set(segmentWords);
    const matched = target.filter((w) => present.has(w)).length;
    const overlap = matched / target.length;
    if (overlap < MIN_WORD_OVERLAP) continue;
    const score = overlap * Math.min(1, matched / segmentWords.length);
    if (score > bestScore) {
      bestIndex = index;
      bestScore = score;
    }
  }
  return bestIndex === -1 ? null : { from: bestIndex, to: bestIndex };
}

export interface ObjectionAnswerExcerpt {
  client: ExcerptTurn;
  following: ExcerptTurn[];
  /** Голоса в стенограмме не разделены — ответ менеджера не выделить. */
  unattributed: boolean;
}

/**
 * Реплика клиента с цитатой и то, что за ней последовало, до
 * `EXCERPT_AGENT_TURNS` реплик менеджера. `null` — цитата в стенограмме не
 * найдена: показывается одна цитата, без придуманного ответа.
 */
export function objectionAnswerExcerpt(
  segments: readonly ExcerptSegment[],
  quote: string
): ObjectionAnswerExcerpt | null {
  const match = locateQuote(segments, quote);
  if (!match) return null;

  const clientLabel = segments[match.from]!.speaker;
  // Labels tell the sides apart only when ASR produced at least two of them;
  // one label for the whole call means the voices were not separated, and a
  // label comparison would read every turn as the customer's.
  const labels = new Set(
    segments.map((s) => s.speaker).filter((label) => label !== undefined)
  );
  const labelsSeparated = labels.size >= 2;
  const anyRole = segments.some((s) => roleOf(s) !== 'unknown');
  if (!labelsSeparated && !anyRole)
    return { client: { speaker: 'client', text: '' }, following: [], unattributed: true };
  const client: ExcerptTurn = {
    speaker: 'client',
    text: segments
      .slice(match.from, match.to + 1)
      .map((s) => (s.text ?? '').trim())
      .join(' '),
  };

  const speakerOf = (segment: ExcerptSegment): ExcerptSpeaker => {
    const role = roleOf(segment);
    if (role !== 'unknown') return role;
    if (!labelsSeparated || clientLabel === undefined || segment.speaker === undefined)
      return 'unknown';
    return segment.speaker === clientLabel ? 'client' : 'agent';
  };

  const following: ExcerptTurn[] = [];
  let agentTurns = 0;
  for (let i = match.to + 1; i < segments.length; i++) {
    const text = (segments[i]!.text ?? '').trim();
    if (text === '') continue;
    const speaker = speakerOf(segments[i]!);
    following.push({ speaker, text });
    if (speaker === 'agent') agentTurns++;
    if (agentTurns >= EXCERPT_AGENT_TURNS || following.length >= EXCERPT_MAX_FOLLOWING)
      break;
  }
  return { client, following, unattributed: false };
}
